import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import axios from "axios";
import { api, setTokens, clearTokens, storedRefreshToken } from "./api";

const BASE = (import.meta.env.VITE_API_URL as string) ?? "";

export interface AdminUser {
  id: string;
  email: string;
  role: string;
  status: string;
  permissions: string[];
  createdAt: string;
}

interface AuthCtx {
  user: AdminUser | null;
  loading: boolean;
  login(email: string, password: string): Promise<void>;
  googleLogin(credential: string): Promise<void>;
  logout(): Promise<void>;
  can(permission: string): boolean;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const rt = storedRefreshToken();
    if (!rt) {
      setLoading(false);
      return;
    }

    axios
      .post<{ accessToken: string; refreshToken: string }>(
        `${BASE}/admin/auth/refresh`,
        { refreshToken: rt }
      )
      .then(({ data }) => {
        setTokens(data.accessToken, data.refreshToken);
        return api.get<AdminUser>("/admin/auth/me");
      })
      .then(({ data }) => setUser(data))
      .catch(() => clearTokens())
      .finally(() => setLoading(false));
  }, []);

  async function login(email: string, password: string) {
    const { data } = await api.post<{
      accessToken: string;
      refreshToken: string;
    }>("/admin/auth/login", { email, password });
    setTokens(data.accessToken, data.refreshToken);
    const { data: me } = await api.get<AdminUser>("/admin/auth/me");
    setUser(me);
  }

  async function googleLogin(credential: string) {
    const { data } = await api.post<{
      accessToken: string;
      refreshToken: string;
    }>("/admin/auth/google", { idToken: credential });
    setTokens(data.accessToken, data.refreshToken);
    const { data: me } = await api.get<AdminUser>("/admin/auth/me");
    setUser(me);
  }

  async function logout() {
    const rt = storedRefreshToken();
    if (rt) {
      try {
        await api.post("/admin/auth/logout", { refreshToken: rt });
      } catch {}
    }
    clearTokens();
    setUser(null);
  }

  function can(permission: string) {
    return user?.permissions?.includes(permission) ?? false;
  }

  return (
    <Ctx.Provider value={{ user, loading, login, googleLogin, logout, can }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuth outside AuthProvider");
  return c;
}
